import {
  Autocomplete,
  MenuItem,
  SxProps,
  TextField,
  Theme
} from '@mui/material';
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason
} from '@mui/material/useAutocomplete';
import React, {
  SyntheticEvent, useCallback, useMemo, useRef
} from 'react';
import { v4 as uuid } from 'uuid';

export interface ETOSelectFieldProps<T> {
  id?: string;
  name: string;
  items: T[];
  error?: string;
  label?: string;
  disabled?: boolean;
  defaultValue?: any;
  autoFocus?: boolean;
  value?: any;
  customSx?: SxProps<Theme>;
  size?: 'small' | 'medium';
  disableClearable?: boolean;
  inputRef?: React.Ref<any>;
  // eslint-disable-next-line no-unused-vars
  itemNameSelector: (item: T) => string;
  // eslint-disable-next-line no-unused-vars
  itemValueSelector: (item: T) => any;
  // eslint-disable-next-line no-unused-vars
  itemDisabledSelector?: (item: T) => boolean;
  handleChange?: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  >;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
}

const ETOSelectField = <T extends object | string>(props: ETOSelectFieldProps<T>) => {
  const {
    id,
    defaultValue,
    disableClearable,
    items,
    name,
    label,
    disabled,
    error,
    value,
    inputRef,
    itemValueSelector,
    itemNameSelector,
    handleChange,
    customSx,
    autoFocus,
    size,
    itemDisabledSelector,
    onFocus,
    onKeyDown,
    onBlur,
  } = props;

  const idRef = useRef(id ?? uuid());

  // eslint-disable-next-line eqeqeq
  const selectedVal = useMemo(
    () => items?.find((a) => itemValueSelector(a) === value),
    [value, items, itemValueSelector],
  );

  const _getOptionLabel = useCallback(
    (item: any) => {
      if (!item) {
        return '';
      }

      try {
        const val = itemNameSelector(item);
        return val ?? '';
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`Failed to display option label. ${e}`);
        return '';
      }
    },
    [itemNameSelector],
  );

  // Looks like v5 fixed the need for this code block, leave just in case
  // eslint-disable-next-line no-unused-vars
  const _renderOption = useCallback(
    (p: any) => p && (
      <MenuItem
        data-testid={itemValueSelector(p)}
        sx={{
          whiteSpace: 'normal',
          wordBreak: 'break-all',
        }}
        key={itemValueSelector(p)}
        value={itemValueSelector(p)}
      >
        {itemNameSelector(p)}
      </MenuItem>
    ),
    [itemValueSelector, itemNameSelector],
  );

  const _onChange = useCallback(
    (
      e: SyntheticEvent<Element, Event>,
      v: any,
      reason: AutocompleteChangeReason,
      // eslint-disable-next-line no-unused-vars
      d?: AutocompleteChangeDetails<any> | undefined,
    ) => {
      // hacky to support existing usage of this control
      const newEvent = e as any;
      newEvent.target = {
        value: v ? itemValueSelector(v) : undefined,
        name,
        id: idRef.current,
        reason,
        multiple: false,
      };
      if (handleChange) handleChange(newEvent);
    },
    [handleChange, itemValueSelector, name],
  );

  return (
    <Autocomplete
      id={idRef.current}
      disabled={!items || disabled}
      options={items}
      getOptionLabel={_getOptionLabel}
      // renderOption={_renderOption}
      value={selectedVal ?? null}
      defaultValue={defaultValue ?? ''}
      disableClearable={disableClearable}
      disabledItemsFocusable={false}
      getOptionDisabled={(item) => (itemDisabledSelector ? itemDisabledSelector(item) : false)}
      onChange={_onChange}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          helperText={error}
          error={Boolean(error)}
          variant="outlined"
          size={size}
          autoFocus={autoFocus}
          name={name}
          label={label}
          sx={[...(Array.isArray(customSx) ? customSx : [customSx])]}
          inputProps={{
            ...params.inputProps,
            'data-testid': `${label}`,
          }}
          SelectProps={{
            MenuProps: {
              sx: { width: 'fit-content' },
            },
          }}
          tabIndex={0}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          inputRef={inputRef}
        />
      )}
    />
  );
};

export default ETOSelectField;
